import type { Card as TCard } from "@presidents/game-engine/src/types";
import { ObfuscatedGameState } from "@presidents/game-engine";
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useGameState } from "../../context/GameRoomState";
import { getInvertedTransform } from "../../utils/animation";
import { diffHand } from "../../utils/compare-hand";

const createMyHandCheapId = (hand: ObfuscatedGameState["myHand"]) => {
  return hand?.cards.map((c) => c._id).join();
};

const findCardIdxs = (idxCards: TCard[], ofCards: TCard[]) => {
  return idxCards.map((idxCard) => {
    return ofCards.findIndex((ofCard) => {
      return idxCard._id === ofCard._id;
    });
  });
};

const getCardEl = (card: TCard) =>
  document.querySelector<HTMLDivElement>(`#card-${card._id}`);

const unsetSwapAnimation = (cardEl?: HTMLDivElement) => {
  if (cardEl) {
    cardEl.style.animation = "none";
    cardEl.style.opacity = "1";
    cardEl.style.transform = "";
    cardEl.style.transition = "";
  }
};
const SWAP_ANIMATION_DURATION = 1500;
const RESET_CACHED_HAND_TIMEOUT = SWAP_ANIMATION_DURATION + 250;
const swapTransition = `transform ${SWAP_ANIMATION_DURATION}ms cubic-bezier(0.83, 0, 0.17, 1)`;

export default function useMyHandAnimationEffect(
  myHandInnerRef: React.RefObject<HTMLDivElement>
) {
  const gameState = useGameState();
  const [cachedMyHand, setCachedMyHand] = useState(gameState?.myHand);
  const myHandCheapId = createMyHandCheapId(gameState?.myHand);
  const cachedMyHandCheapId = createMyHandCheapId(cachedMyHand);

  const lastHandRef = useRef(gameState?.myHand);
  const removedCardsRef = useRef<TCard[] | null>(null);
  const addedCardsRef = useRef<TCard[] | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    let ret = () => {};
    const added = diffHand(gameState?.myHand, lastHandRef.current);
    const removed = diffHand(lastHandRef.current, gameState?.myHand);
    const nextMyHand = gameState?.myHand;
    if (gameState?.myHand && added?.length && removed?.length) {
      // Clone
      const intermediateNextMyHand = {
        ...gameState.myHand,
        cards: [...gameState.myHand.cards]
      };
      // Temporaryily show removed card;
      if (lastHandRef?.current) {
        const idxs = findCardIdxs(removed, lastHandRef.current.cards);
        removed.forEach((c, i) => {
          const idx = idxs[i];
          intermediateNextMyHand!.cards = [
            ...intermediateNextMyHand!.cards.slice(0, idx),
            c,
            ...intermediateNextMyHand!.cards.slice(idx)
          ];
        });
        // Set this ref for use in the next tick (useLayoutEffect)
        removedCardsRef.current = removed;
        addedCardsRef.current = added;
      }
      // Set a hand on screen with all the cards...
      setCachedMyHand(intermediateNextMyHand);

      // Revert the cached data after the animation completes
      const tid = window.setTimeout(() => {
        removedCardsRef.current = null;
        addedCardsRef.current = null;
        setCachedMyHand(nextMyHand);
        if (myHandInnerRef.current) {
          myHandInnerRef.current.style.overflowX = "";
        }
      }, RESET_CACHED_HAND_TIMEOUT);
      ret = () => window.clearTimeout(tid);
    } else {
      setCachedMyHand(nextMyHand);
    }
    lastHandRef.current = gameState?.myHand;
    return ret;
  }, [myHandCheapId]);

  useLayoutEffect(() => {
    if (addedCardsRef.current && removedCardsRef.current) {
      const deltTo = addedCardsRef.current.map((c) => c.deltTo)[0];
      const q = `#other-hand-${deltTo} > [data-mini-card]`;
      const otherPlayerEl = document.querySelector<HTMLDivElement>(q);
      const otherPlayerRect = otherPlayerEl?.getBoundingClientRect();

      let unlockedContainer = false;

      const swapCardAnimation = (c: TCard, action: "removed" | "added") => {
        const cardEl = getCardEl(c);
        const to = otherPlayerRect;
        const from = cardEl?.getBoundingClientRect();
        if (cardEl && to && from) {
          const transform = getInvertedTransform(to, from);

          cardEl.style.animation = "none";
          cardEl.style.opacity = "1";

          if (action === "removed") {
            // cardEl.style.backgroundColor = "pink";
            cardEl.style.transition = swapTransition;
          } else {
            // cardEl.style.backgroundColor = "limegreen";
            cardEl.style.transition = "none";
            cardEl.style.transform = transform;
          }

          if (!unlockedContainer && myHandInnerRef.current) {
            myHandInnerRef.current!.style.overflowX = "unset";
            unlockedContainer = true;
          }
          requestAnimationFrame(() => {
            if (cardEl) {
              if (action === "removed") {
                cardEl.style.transform = transform;
              } else {
                cardEl.style.transition = swapTransition;
                cardEl.style.transform = "";
              }
              setTimeout(() => {
                unsetSwapAnimation(cardEl);
              }, RESET_CACHED_HAND_TIMEOUT);
            }
          });
        }
      };

      removedCardsRef.current.forEach((c) => {
        swapCardAnimation(c, "removed");
      });

      addedCardsRef.current.forEach((c) => {
        swapCardAnimation(c, "added");
      });
    }
  }, [cachedMyHandCheapId]);

  return cachedMyHand;
}
